/** Search page behavior: the Change View control, and the predictive-first swap. */

import { animateCardsIn } from '@theme/card-enter';
import { announce } from '@theme/component';

// The API's own maximum. This is the full-page surface, so it asks for all of
// them; the merchant-facing setting documents that broad terms cap out here.
const PREDICTIVE_LIMIT = 10;

const DEFAULT_FIELDS = 'title,product_type,tag,vendor,variants.title';

const RESULTS_LIST = '[data-search-results-list]';

// Cards inside the swapped region, in visual order.
const RESULT_CARDS = '.g-collection__card';

/**
 * Builds the locale-aware Predictive Search URL that re-renders this section.
 *
 * @param {object} options
 * @param {string} options.rootUrl - Shopify locale root, e.g. "/" or "/fr-ca".
 * @param {string} options.term - Raw search term.
 * @param {string} options.origin - Absolute origin used to resolve the URL.
 * @param {string} options.sectionId - This section's rendered id.
 * @param {number} [options.limit] - Maximum products to request; API max is 10.
 * @param {string} [options.fields] - Comma-separated fields to index.
 * @returns {URL}
 */
export function buildPredictiveUrl({
  rootUrl = '/',
  term,
  origin,
  sectionId,
  limit = PREDICTIVE_LIMIT,
  fields = DEFAULT_FIELDS,
}) {
  const base = `${String(rootUrl).replace(/\/$/, '')}/search/suggest`;
  const url = new URL(base, origin);
  url.searchParams.set('q', term);
  url.searchParams.set('resources[type]', 'product');
  url.searchParams.set('resources[limit]', String(limit));
  url.searchParams.set('resources[options][unavailable_products]', 'show');
  url.searchParams.set('resources[options][fields]', fields);
  url.searchParams.set('section_id', sectionId);
  return url;
}

/**
 * Whether this document came from a reload or history traversal rather than a new request.
 *
 * @returns {boolean}
 */
function isRestoredNavigation() {
  const type = performance.getEntriesByType?.('navigation')[0]?.type;
  return type === 'reload' || type === 'back_forward';
}

class SearchResults extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();

    this.changeViewButton = this.querySelector('[data-collection-change-view]');
    if (this.changeViewButton) {
      this.changeViewButton.disabled = false;
      this.changeViewButton.removeAttribute('aria-disabled');
      this.addEventListener('click', this.onClick, { signal: this.controller.signal });
    }

    if (this.hasAttribute('data-predictive-first')) this.loadPredictive();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.animations?.forEach((animation) => animation.cancel());
    this.animations = null;
    if (this.changeViewButton) {
      this.changeViewButton.disabled = true;
      this.changeViewButton.setAttribute('aria-disabled', 'true');
    }
  }

  onClick = (event) => {
    if (!event.target.closest('[data-collection-change-view]')) return;
    const showAlternate = this.dataset.changeView !== 'alternate';
    this.dataset.changeView = showAlternate ? 'alternate' : 'primary';
    this.changeViewButton?.setAttribute('aria-pressed', String(showAlternate));
  };

  /** Fetches a predictive render of this section and hands it to `applyPredictive`. */
  async loadPredictive() {
    const term = this.dataset.searchTerms;
    const sectionId = this.dataset.sectionId;
    if (!term || !sectionId || this.hasAttribute('data-predictive-fallback')) return;

    const url = buildPredictiveUrl({
      rootUrl: window.Shopify?.routes?.root || '/',
      term,
      origin: window.location.origin,
      sectionId,
      fields: this.dataset.searchFields || DEFAULT_FIELDS,
    });

    try {
      const response = await fetch(url, {
        signal: this.controller.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) return;

      const markup = await response.text();
      if (this.controller?.signal.aborted) return;

      const incoming = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector(RESULTS_LIST);
      if (!incoming) return;

      const count = Number.parseInt(incoming.dataset.searchResultCount || '0', 10) || 0;
      if (count < 1 || this.hasAttribute('data-predictive-fallback')) return;

      this.applyPredictive(incoming);
    } catch {
    } finally {
      this.settle();
    }
  }

  settle() {
    this.removeAttribute('data-predictive-pending');
    this.querySelector('.g-collection__results')?.removeAttribute('aria-busy');
  }

  /** Swaps the results region, and brings the header into agreement with it. */
  applyPredictive(incoming) {
    const current = this.querySelector(RESULTS_LIST);
    if (!current) return;

    current.replaceWith(incoming);

    const title = this.querySelector('[data-search-title]');
    const statedTitle = title?.textContent.trim() ?? '';
    if (title && this.dataset.resultsStateTitle) {
      title.textContent = this.dataset.resultsStateTitle;
    }
    const stateChanged = Boolean(title) && title.textContent.trim() !== statedTitle;

    const actions = this.querySelector('.g-collection__actions');
    if (actions) actions.hidden = false;

    this.toggleAttribute('data-predictive-active', true);

    this.settle();
    if (stateChanged && !isRestoredNavigation()) this.announceCount(incoming);
    this.animations = animateCardsIn(incoming.querySelectorAll(RESULT_CARDS));
  }

  announceCount(incoming) {
    const status = this.querySelector('[data-search-results-status]');
    const template = status?.dataset.countTemplate;
    if (!template) return;
    const count = Number.parseInt(incoming.dataset.searchResultCount || '0', 10) || 0;
    announce(
      status,
      template.replace('__COUNT__', String(count)).replace('__QUERY__', () => this.dataset.searchTerms || ''),
    );
  }
}

if (!customElements.get('search-results')) {
  customElements.define('search-results', SearchResults);
}
